import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Building2, MapPin, Maximize, Calendar, CreditCard, FileText, Image, AlertTriangle, Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatCurrency, formatArea, getPropertyTypeLabel } from '@/lib/utils';
import type { SharedPropertyData } from '@/types/sharing';
import type { PropertyType } from '@/types';

export default function ShareView() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedPropertyData | null>(null);
  const [error, setError] = useState<'not_found' | 'expired' | 'loading' | null>('loading');
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token) return;
    loadSharedProperty(token);
  }, [token]);

  const loadSharedProperty = async (t: string) => {
    try {
      const { data: result, error: rpcError } = await supabase.rpc('get_shared_property', {
        p_token: t,
      });

      if (rpcError) throw rpcError;

      if (!result || result.error) {
        setError((result?.error as 'not_found' | 'expired') || 'not_found');
        return;
      }

      setData(result as SharedPropertyData);
      setError(null);

      // Bilder laden wenn vorhanden
      if (result.images && Array.isArray(result.images) && result.images.length > 0) {
        loadImageUrls(t, result.images.map((img: any) => img.storage_path));
      }
    } catch {
      setError('not_found');
    }
  };

  const loadImageUrls = async (_t: string, paths: string[]) => {
    // Signed URLs über den authenticated supabase client generieren
    // Da Storage private ist, müssen wir einen Workaround verwenden:
    // Wir erstellen signed URLs für jeden Pfad über die Storage API
    // (anon key reicht für öffentliche Buckets, für private braucht man signierte URLs)
    const urls: Record<string, string> = {};
    await Promise.all(
      paths.map(async (path) => {
        const { data: urlData } = await supabase.storage
          .from('property-images')
          .createSignedUrl(path, 3600);
        if (urlData?.signedUrl) {
          urls[path] = urlData.signedUrl;
        }
      })
    );
    setImageUrls(urls);
  };

  if (error === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
          <p className="text-foreground-muted text-sm">Wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-warning/10 flex items-center justify-center mx-auto">
            <Clock className="w-7 h-7 text-warning" />
          </div>
          <h1 className="text-xl font-semibold">Link abgelaufen</h1>
          <p className="text-foreground-muted">Dieser Freigabe-Link ist nicht mehr gültig. Bitte fordere einen neuen Link an.</p>
        </div>
      </div>
    );
  }

  if (error === 'not_found' || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <AlertTriangle className="w-7 h-7 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold">Link nicht gefunden</h1>
          <p className="text-foreground-muted">Dieser Freigabe-Link existiert nicht oder wurde gelöscht.</p>
        </div>
      </div>
    );
  }

  const { permissions } = data;
  const address = [data.address_street, data.address_zip, data.address_city]
    .filter(Boolean)
    .join(', ');

  const primaryImage = data.images?.find((img) => img.is_primary) || data.images?.[0];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-lg sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-foreground flex items-center justify-center">
              <Building2 className="w-3.5 h-3.5 text-background" />
            </div>
            <span className="font-semibold text-base">Fundament</span>
          </Link>
          {data.link_name && (
            <span className="text-xs text-foreground-muted bg-background-subtle px-3 py-1 rounded-full">
              {data.link_name}
            </span>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Hero */}
        <div className="space-y-2">
          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background-subtle text-foreground-muted">
              {getPropertyTypeLabel(data.property_type as PropertyType)}
            </span>
          </div>
          <h1 className="text-2xl font-semibold">{data.name}</h1>
          {address && (
            <div className="flex items-center gap-1.5 text-foreground-muted">
              <MapPin className="w-4 h-4 shrink-0" />
              <span className="text-sm">{address}</span>
            </div>
          )}
        </div>

        {/* Primärbild */}
        {permissions.photos && primaryImage && imageUrls[primaryImage.storage_path] && (
          <div className="rounded-xl overflow-hidden aspect-video bg-background-subtle">
            <img
              src={imageUrls[primaryImage.storage_path]}
              alt={data.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Basisdaten */}
        {permissions.basic_info && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.living_area_sqm && (
              <div className="bg-background-subtle rounded-xl p-4">
                <div className="flex items-center gap-1.5 text-foreground-muted mb-1">
                  <Maximize className="w-3.5 h-3.5" />
                  <span className="text-xs">Wohnfläche</span>
                </div>
                <p className="font-semibold">{formatArea(data.living_area_sqm)}</p>
              </div>
            )}
            {data.year_built && (
              <div className="bg-background-subtle rounded-xl p-4">
                <div className="flex items-center gap-1.5 text-foreground-muted mb-1">
                  <Calendar className="w-3.5 h-3.5" />
                  <span className="text-xs">Baujahr</span>
                </div>
                <p className="font-semibold">{data.year_built}</p>
              </div>
            )}
            {data.num_units && data.num_units > 1 && (
              <div className="bg-background-subtle rounded-xl p-4">
                <div className="flex items-center gap-1.5 text-foreground-muted mb-1">
                  <Building2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Einheiten</span>
                </div>
                <p className="font-semibold">{data.num_units}</p>
              </div>
            )}
            {data.land_area_sqm && (
              <div className="bg-background-subtle rounded-xl p-4">
                <div className="flex items-center gap-1.5 text-foreground-muted mb-1">
                  <Maximize className="w-3.5 h-3.5" />
                  <span className="text-xs">Grundstück</span>
                </div>
                <p className="font-semibold">{formatArea(data.land_area_sqm)}</p>
              </div>
            )}
          </div>
        )}

        {/* Finanzielle Daten */}
        {(permissions.purchase_price || permissions.current_value || permissions.rental_income) && (
          <div className="bg-background-subtle rounded-xl p-5 space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-foreground-muted" />
              Finanzdaten
            </h2>
            <div className="space-y-2">
              {permissions.purchase_price && data.purchase_price != null && (
                <div className="flex justify-between">
                  <span className="text-foreground-muted text-sm">Kaufpreis</span>
                  <span className="font-medium">{formatCurrency(data.purchase_price)}</span>
                </div>
              )}
              {permissions.purchase_price && (
                (() => {
                  const additionalCosts =
                    (data.cost_transfer_tax || 0) +
                    (data.cost_notary || 0) +
                    (data.cost_land_registry || 0) +
                    (data.cost_broker || 0) +
                    (data.cost_other || 0) ||
                    (data.additional_costs || 0);
                  return additionalCosts > 0 ? (
                    <div className="flex justify-between">
                      <span className="text-foreground-muted text-sm">Kaufnebenkosten</span>
                      <span className="font-medium">{formatCurrency(additionalCosts)}</span>
                    </div>
                  ) : null;
                })()
              )}
              {permissions.current_value && data.current_value != null && (
                <div className="flex justify-between">
                  <span className="text-foreground-muted text-sm">Aktueller Wert</span>
                  <span className="font-medium text-accent-green">{formatCurrency(data.current_value)}</span>
                </div>
              )}
              {permissions.rental_income && data.rent_cold != null && (
                <div className="flex justify-between">
                  <span className="text-foreground-muted text-sm">Miete (kalt)</span>
                  <span className="font-medium">{formatCurrency(data.rent_cold)}</span>
                </div>
              )}
              {permissions.renovation_costs && data.renovation_cost_total != null && data.renovation_cost_total > 0 && (
                <div className="flex justify-between">
                  <span className="text-foreground-muted text-sm">Sanierungskosten</span>
                  <span className="font-medium">{formatCurrency(data.renovation_cost_total)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Finanzierung */}
        {permissions.financing && data.financings && data.financings.length > 0 && (
          <div className="bg-background-subtle rounded-xl p-5 space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-foreground-muted" />
              Finanzierung
            </h2>
            {data.financings.map((f) => (
              <div key={f.id} className="space-y-2">
                {f.bank_name && (
                  <p className="font-medium text-sm">{f.bank_name}</p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div>
                    <p className="text-xs text-foreground-muted">Darlehensbetrag</p>
                    <p className="font-medium">{formatCurrency(f.loan_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-muted">Zinssatz</p>
                    <p className="font-medium">{f.interest_rate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-muted">Tilgung</p>
                    <p className="font-medium">{f.repayment_rate}%</p>
                  </div>
                  {f.monthly_payment != null && (
                    <div>
                      <p className="text-xs text-foreground-muted">Monatliche Rate</p>
                      <p className="font-medium">{formatCurrency(f.monthly_payment)}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Weitere Bilder */}
        {permissions.photos && data.images && data.images.length > 1 && (
          <div className="space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <Image className="w-4 h-4 text-foreground-muted" />
              Fotos
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {data.images.map((img) => {
                const url = imageUrls[img.storage_path];
                if (!url || img.is_primary) return null;
                return (
                  <div key={img.id} className="aspect-square rounded-lg overflow-hidden bg-background-subtle">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dokumente */}
        {permissions.documents && data.documents && data.documents.length > 0 && (
          <div className="bg-background-subtle rounded-xl p-5 space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-foreground-muted" />
              Dokumente
            </h2>
            <div className="space-y-2">
              {data.documents.map((doc) => (
                <div key={doc.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <FileText className="w-4 h-4 text-foreground-muted shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{doc.name}</p>
                    {doc.file_size && (
                      <p className="text-xs text-foreground-muted">
                        {(doc.file_size / 1024 / 1024).toFixed(1)} MB
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-border pt-6 text-center">
          <p className="text-xs text-foreground-muted">
            Geteilt mit{' '}
            <Link to="/" className="font-medium text-foreground hover:underline">
              Fundament
            </Link>{' '}
            · Immobilien-Portfolio-Management
          </p>
        </div>
      </main>
    </div>
  );
}
